import { Injectable } from '@angular/core';
import { OptionsModel } from '@d-components/form/form.interface';
import {
  ReferenceDataModel,
  REFERENCE_TYPES,
} from '@d-models/services/reference-data.model';

import { CommonService, HttpService } from '@d-services';
import { forkJoin, mergeMap, Subject, tap, of } from 'rxjs';

export type ReferenceData = {
  [key in REFERENCE_TYPES]?: ReferenceDataModel[];
};

@Injectable({
  providedIn: 'root',
})
export class ReferenceDataService {
  pathUrl: string = 'master-data/reference-data';

  private referencesData: ReferenceData = {};

  loaded$ = new Subject<boolean>();

  constructor(
    private httpService: HttpService,
    private commonService: CommonService,
  ) {}

  getAllCountry() {
    return this.commonService.getCountries().pipe(
      tap((val: any) => {
        this.referencesData[REFERENCE_TYPES.COUNTRY] = val.data.map(
          (item: any) => ({
            dataCode: item.id,
            dataValue: item.name,
            dataType: REFERENCE_TYPES.COUNTRY,
            code: item.code,
          }),
        );
      }),
    );
  }

  getAllStates() {
    return this.commonService.getAllStates().pipe(
      tap((val) => {
        this.referencesData[REFERENCE_TYPES.STATES] = val.data.map(
          (item: any) => ({
            dataCode: item.id,
            dataValue: item.name,
            dataType: REFERENCE_TYPES.STATES,
            code: item.code,
          }),
        );
      }),
    );
  }

  getAll() {
    return this.httpService
      .getAll<ReferenceDataModel>(`${this.pathUrl}/get-all`, ReferenceDataModel)
      .pipe(
        tap((res: any) => {
          res.forEach((item: ReferenceDataModel) => {
            if (!this.referencesData[item.dataType]) {
              this.referencesData[item.dataType] = [item];
            } else {
              this.referencesData[item.dataType] = [
                ...(this.referencesData[item.dataType] || []),
                item,
              ];
            }
          });
        }),
      );
  }

  getAllByType(type: string) {
    return this.httpService.getAll<any>(
      `${this.pathUrl}/${type}/search`,
      ReferenceDataModel,
    );
  }

  getStoredAllByType(type: REFERENCE_TYPES) {
    const data = this.referencesData[type];
    if (data) return of(data);
    return this.getByTypeAndStore(type);
  }

  getTypes() {
    return this.httpService
      .getAll<any>(`${this.pathUrl}/get-all-type`, String)
      .pipe(
        mergeMap((res: any) => {
          const requests = res.map((type: REFERENCE_TYPES) =>
            this.getByTypeAndStore(type),
          );
          return forkJoin(requests).pipe(
            tap(() => {
              this.loaded$.next(true);
            }),
          );
        }),
      );
  }

  getByTypeAndStore(type: REFERENCE_TYPES) {
    return this.httpService
      .getAll<any>(`${this.pathUrl}/${type}/search`, ReferenceDataModel)
      .pipe(
        tap((res) => {
          this.referencesData[type] = res;
        }),
      );
  }

  getDataByDataCode(dataCode: string, referenceDataKey: REFERENCE_TYPES) {
    const referenceData = this.referencesData[referenceDataKey];
    if (!referenceData) {
      return '';
    }
    const data = referenceData.find((d) => d.dataCode === dataCode);
    if (!data) {
      return '';
    }
    return data.dataValue;
  }

  getDataByCode(code: string, referenceDataKey: REFERENCE_TYPES) {
    const referenceData = this.referencesData[referenceDataKey];
    if (!referenceData) {
      return '';
    }
    const data = referenceData.find((d) => d.code === code);
    if (!data) {
      return '';
    }
    return data.dataValue;
  }

  getOptionsByType(referenceDataKey: REFERENCE_TYPES): OptionsModel[] {
    const referenceData = this.referencesData[referenceDataKey];
    if (!referenceData || !referenceData.length) {
      return [];
    }
    return referenceData.map((item) => ({
      value: item.dataCode,
      label: item.dataValue,
    }));
  }
}
